import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { getAuthToken } from '../../utils/auth';

// ✅ ENTERPRISE FIX: Never send `Authorization: Bearer null`
const getSafeAccessToken = () => {
  try {
    const raw = getAuthToken() || '';
    const token = raw ? String(raw).trim() : '';
    const lower = token.toLowerCase();
    const isJwtLike = token.split('.').length >= 3;
    if (!token || lower === 'null' || lower === 'undefined' || !isJwtLike) return null;
    return token;
  } catch (_) {
    return null;
  }
};

const getAuthHeader = () => {
  const token = getSafeAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Async thunks for API calls
export const fetchPlatformAnalytics = createAsyncThunk(
  'analytics/fetchPlatformAnalytics',
  async ({ period = '30d', granularity = 'day', tenantId = null }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        period,
        granularity,
        ...(tenantId && { tenantId }),
      });

      const response = await fetch(`/api/super-admin/analytics/platform?${params}`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch platform analytics');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRealTimeMetrics = createAsyncThunk(
  'analytics/fetchRealTimeMetrics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/super-admin/analytics/realtime', {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch real-time metrics');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTenantAnalytics = createAsyncThunk(
  'analytics/fetchTenantAnalytics',
  async ({ tenantId, period = '30d', granularity = 'day' }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        period,
        granularity,
      });

      const response = await fetch(`/api/super-admin/analytics/tenants/${tenantId}?${params}`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tenant analytics');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSystemHealth = createAsyncThunk(
  'analytics/fetchSystemHealth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/super-admin/system/health', {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch system health');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAuditLogs = createAsyncThunk(
  'analytics/fetchAuditLogs',
  async ({ page = 1, limit = 50, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters,
      });

      const response = await fetch(`/api/super-admin/audit/logs?${params}`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUsers = createAsyncThunk(
  'analytics/fetchUsers',
  async ({ page = 1, limit = 50, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters,
      });

      const response = await fetch(`/api/super-admin/users?${params}`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTenants = createAsyncThunk(
  'analytics/fetchTenants',
  async ({ page = 1, limit = 50, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters,
      });

      const response = await fetch(`/api/superadmin/tenants?${params}`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tenants');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendSystemNotification = createAsyncThunk(
  'analytics/sendSystemNotification',
  async (notification, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/super-admin/notifications/system', {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(notification),
      });

      if (!response.ok) {
        throw new Error('Failed to send system notification');
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const performBulkOperation = createAsyncThunk(
  'analytics/performBulkOperation',
  async ({ operation, data }, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/super-admin/bulk/${operation}`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to perform bulk ${operation}`);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const exportData = createAsyncThunk(
  'analytics/exportData',
  async ({ type, format = 'json', filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        format,
        ...filters,
      });

      const response = await fetch(`/api/super-admin/${type}/export?${params}`, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to export ${type} data`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);

      return { success: true, type, format };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  // Platform analytics
  platformAnalytics: {
    users: {
      total: 0,
      active: 0,
      daily: [],
      monthly: [],
    },
    tenants: {
      total: 0,
      active: 0,
      byPlan: [],
      byStatus: [],
    },
    documents: {
      total: 0,
      processed: 0,
      daily: [],
      byType: [],
    },
    cases: {
      total: 0,
      active: 0,
      byStatus: [],
      byType: [],
    },
    activity: {
      daily: [],
      byType: [],
      topUsers: [],
    },
    revenue: {
      total: 0,
      daily: [],
      byPlan: [],
    },
  },

  // Real-time metrics
  realTimeMetrics: {
    activeUsers: 0,
    activeTenants: 0,
    documentsProcessed: 0,
    systemLoad: {
      cpu: 0,
      memory: 0,
      disk: 0,
      network: 0,
    },
    websocketStats: {
      connections: 0,
      messagesPerSecond: 0,
      averageLatency: 0,
    },
    alerts: [],
  },

  // System health
  systemHealth: {
    uptime: 0,
    platform: '',
    nodeVersion: '',
    database: {
      collections: 0,
      dataSize: 0,
      storageSize: 0,
      status: 'healthy',
    },
    services: {
      email: 'healthy',
      cache: 'healthy',
      websocket: 'healthy',
    },
  },

  // Audit logs
  auditLogs: [],
  auditLogsPagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  },

  // Users
  users: [],
  usersPagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  },

  // Tenants
  tenants: [],
  tenantsPagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  },

  // UI state
  loading: false,
  error: null,
  lastUpdated: null,

  // Filters and preferences
  filters: {
    timeRange: '30d',
    granularity: 'day',
    searchTerm: '',
    selectedTenant: null,
  },

  // Notifications
  notifications: [],
  unreadNotifications: 0,
};

// Analytics slice
const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    // Update real-time metrics from WebSocket
    updateRealTimeMetrics: (state, action) => {
      state.realTimeMetrics = {
        ...state.realTimeMetrics,
        ...action.payload,
      };
      state.lastUpdated = new Date().toISOString();
    },

    // Add system notification
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload);
      state.unreadNotifications += 1;
    },

    // Mark notifications as read
    markNotificationsAsRead: state => {
      state.unreadNotifications = 0;
    },

    // Update filters
    updateFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    // Clear error
    clearError: state => {
      state.error = null;
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Update selected tenant
    setSelectedTenant: (state, action) => {
      state.filters.selectedTenant = action.payload;
    },

    // Add alert
    addAlert: (state, action) => {
      state.realTimeMetrics.alerts.unshift(action.payload);
      if (state.realTimeMetrics.alerts.length > 10) {
        state.realTimeMetrics.alerts.pop();
      }
    },

    // Clear alerts
    clearAlerts: state => {
      state.realTimeMetrics.alerts = [];
    },
  },
  extraReducers: builder => {
    // Platform Analytics
    builder
      .addCase(fetchPlatformAnalytics.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlatformAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.platformAnalytics = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchPlatformAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Real-time Metrics
    builder
      .addCase(fetchRealTimeMetrics.pending, state => {
        state.error = null;
      })
      .addCase(fetchRealTimeMetrics.fulfilled, (state, action) => {
        state.realTimeMetrics = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchRealTimeMetrics.rejected, (state, action) => {
        state.error = action.payload;
      });

    // Tenant Analytics
    builder
      .addCase(fetchTenantAnalytics.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenantAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        // Store tenant-specific analytics
        state.tenantAnalytics = action.payload;
      })
      .addCase(fetchTenantAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // System Health
    builder
      .addCase(fetchSystemHealth.pending, state => {
        state.error = null;
      })
      .addCase(fetchSystemHealth.fulfilled, (state, action) => {
        state.systemHealth = action.payload;
      })
      .addCase(fetchSystemHealth.rejected, (state, action) => {
        state.error = action.payload;
      });

    // Audit Logs
    builder
      .addCase(fetchAuditLogs.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.auditLogs = action.payload.logs;
        state.auditLogsPagination = action.payload.pagination;
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Users
    builder
      .addCase(fetchUsers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.usersPagination = action.payload.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Tenants
    builder
      .addCase(fetchTenants.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.loading = false;
        state.tenants = action.payload.tenants;
        state.tenantsPagination = action.payload.pagination;
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // System Notification
    builder.addCase(sendSystemNotification.fulfilled, (state, action) => {
      // Add to notifications list
      state.notifications.unshift({
        id: Date.now(),
        type: 'system',
        title: 'System Notification Sent',
        message: 'Notification has been sent successfully',
        timestamp: new Date().toISOString(),
        read: false,
      });
    });

    // Bulk Operations
    builder.addCase(performBulkOperation.fulfilled, (state, action) => {
      // Add success notification
      state.notifications.unshift({
        id: Date.now(),
        type: 'success',
        title: 'Bulk Operation Completed',
        message: `Bulk operation "${action.payload.operation}" completed successfully`,
        timestamp: new Date().toISOString(),
        read: false,
      });
    });

    // Export Data
    builder.addCase(exportData.fulfilled, (state, action) => {
      // Add success notification
      state.notifications.unshift({
        id: Date.now(),
        type: 'success',
        title: 'Data Exported',
        message: `${action.payload.type} data exported successfully as ${action.payload.format}`,
        timestamp: new Date().toISOString(),
        read: false,
      });
    });
  },
});

// Export actions
export const {
  updateRealTimeMetrics,
  addNotification,
  markNotificationsAsRead,
  updateFilters,
  clearError,
  setLoading,
  setSelectedTenant,
  addAlert,
  clearAlerts,
} = analyticsSlice.actions;

// Export selectors
export const selectPlatformAnalytics = state => state.analytics.platformAnalytics;
export const selectRealTimeMetrics = state => state.analytics.realTimeMetrics;
export const selectSystemHealth = state => state.analytics.systemHealth;
export const selectAuditLogs = state => state.analytics.auditLogs;
export const selectUsers = state => state.analytics.users;
export const selectTenants = state => state.analytics.tenants;
export const selectAnalyticsLoading = state => state.analytics.loading;
export const selectAnalyticsError = state => state.analytics.error;
export const selectAnalyticsFilters = state => state.analytics.filters;
export const selectNotifications = state => state.analytics.notifications;
export const selectUnreadNotifications = state => state.analytics.unreadNotifications;
export const selectLastUpdated = state => state.analytics.lastUpdated;

// Export reducer
export default analyticsSlice.reducer;
